// src/pages/DealerVehicleListing.tsx

import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  CircularProgress,
  Typography,
  Grid,
  Stack,
  Pagination,
  Button,
  Tooltip,
  Container,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useState, useEffect } from 'react';
import NotFoundPage from './NotFoundPage';
import CarCard from '../components/CarCard';
import CarFilterBar from '../components/CarFilterBar';
import NewCarCard from '../components/NewCarCard';
import NewCarFilterBar from '../components/NewCarFilterBar';
import axiosInstance from '../services/axiosInstance';
import OtherBranchDialog from '../components/OtherBranchDialog';

// --- Icons ---
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FaxIcon from '@mui/icons-material/Print';
import StorefrontIcon from '@mui/icons-material/Storefront';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import SmartphoneIcon from '@mui/icons-material/Smartphone';

// ✅ Import the decoder
import { decodeId } from '../utils/idEncoder';

interface HomePageProps {
  mode: 'light' | 'dark';
  setMode: (mode: 'light' | 'dark') => void;
}

const DealerVehicleListing = ({ mode, setMode }: HomePageProps) => {
  const { id: hashId } = useParams<{ id: string }>();
  const realDealerId = hashId ? decodeId(hashId) : null;

  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  // ✅ Determine Condition (Used vs New), default to 'used'
  const condition = searchParams.get('condition') || 'used';

  // ✅ The keys that require decoding back into integers for the API
  const idListKeys = [
    'vehicleTransmissionTypeIds',
    'vehicleFuelTypeIds',
    'vehicleBrandIds',
    'vehicleBodyTypeIds',
    'vehicleClassIds',
  ];

  const parseQueryParam = (key: string): number[] => {
    const raw = searchParams.get(key);
    if (!raw) return [];

    const parts = raw.split(',');

    // If the parameter is an encrypted ID array, decode it
    if (idListKeys.includes(key)) {
      return parts
        .map(hash => decodeId(hash))
        .filter((id): id is number => id !== null);
    }

    // Fallback for standard numbers
    return parts.map(Number);
  };

  // Shared & Used Params (Now safely decoded!)
  const vehicleTransmissionTypeIds = parseQueryParam('vehicleTransmissionTypeIds');
  const vehicleFuelTypeIds = parseQueryParam('vehicleFuelTypeIds');
  const vehicleBrandIds = parseQueryParam('vehicleBrandIds');
  const vehicleBodyTypeIds = parseQueryParam('vehicleBodyTypeIds');
  const vehicleClassIds = parseQueryParam('vehicleClassIds');
  const statusIdString = searchParams.get('statusId');
  const statusID = statusIdString ? statusIdString.split(',') : [];
  
  // Used Price Params
  const minAskingPrice = searchParams.get('minAskingPrice') ? Number(searchParams.get('minAskingPrice')) : null;
  const maxAskingPrice = searchParams.get('maxAskingPrice') ? Number(searchParams.get('maxAskingPrice')) : null;

  // New Price Params
  const minPrice = searchParams.get('minPrice') ? Number(searchParams.get('minPrice')) : null;
  const maxPrice = searchParams.get('maxPrice') ? Number(searchParams.get('maxPrice')) : null;

  // Other Used Params
  const minManufacturingYear = searchParams.get('minManufacturingYear') ? Number(searchParams.get('minManufacturingYear')) : null;
  const maxManufacturingYear = searchParams.get('maxManufacturingYear') ? Number(searchParams.get('maxManufacturingYear')) : null;
  const minMileage = searchParams.get('minMileage') ? Number(searchParams.get('minMileage')) : null;
  const maxMileage = searchParams.get('maxMileage') ? Number(searchParams.get('maxMileage')) : null;
  const minDepreciationValue = searchParams.get('minDepreciation') ? Number(searchParams.get('minDepreciation')) : null;
  const maxDepreciationValue = searchParams.get('maxDepreciation') ? Number(searchParams.get('maxDepreciation')) : null;
  
  const vehicleModel = searchParams.get('vehicleModel') || '';

  // State
  const [vehicleList, setVehicleList] = useState<any[] | null>(null);
  const [vehicleDealerInfo, setDealerInfo] = useState<any | null>(null);
  const [otherBranch, setOtherBranch] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [branchDialogOpen, setBranchDialogOpen] = useState(false);

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 10;

  const fetchVehicle = async () => {
    if (!realDealerId) return;

    try {
      setLoading(true);
      let endpoint = '';
      let payload: any = {};

      // ✅ Dynamic Payload based on Condition
      if (condition === 'new') {
        endpoint = '/CustomerVehicle/NewVehicleGetListPublished';
        payload = {
          dealerIds: [realDealerId],
          vehicleBrandIds: vehicleBrandIds.length > 0 ? vehicleBrandIds : null,
          vehicleBodyTypeIds: vehicleBodyTypeIds.length > 0 ? vehicleBodyTypeIds : null,
          vehicleFuelTypeIds: vehicleFuelTypeIds.length > 0 ? vehicleFuelTypeIds : null,
          minPrice,
          maxPrice,
          searchKeyword: searchQuery,
          pageNumber: currentPage,
          pageSize: itemsPerPage,
        };
      } else {
        endpoint = '/CustomerVehicle/VehicleGetListPublished';
        payload = {
          dealerIds: [realDealerId],
          vehicleTransmissionTypeIds,
          vehicleFuelTypeIds,
          vehicleBrandIds,
          vehicleClassIds,
          vehicleBodyTypeIds,
          minAskingPrice,
          maxAskingPrice,
          minManufacturingYear,
          maxManufacturingYear,
          minMileage,
          maxMileage,
          minDepreciationValue,
          maxDepreciationValue,
          statusID,
          pageNumber: currentPage,
          pageSize: itemsPerPage,
          searchKeyword: searchQuery, 
          vehicleModel: vehicleModel 
        };
      }

      const response = await axiosInstance.post(endpoint, payload);
      const data = response.data as any;
      
      const responseData = data.responseData || data; // Handle slight structure differences
      
      setVehicleList(responseData?.vehicleList || responseData?.VehicleList || []);
      setTotalRecords(responseData?.totalRecords ?? responseData?.TotalRecords ?? 0);
      setTotalPages(responseData?.totalPages ?? responseData?.TotalPages ?? 0);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch vehicle data.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDealer = async () => {
    if (!realDealerId) return;
    try {
      const response = await axiosInstance.get(`/CustomerVehicle/DealerGetByCompany/${realDealerId}`);
      const data = response.data as any;
      const branchList = data.responseData?.otherBranch || [];
      setDealerInfo(data.responseData || null);
      setOtherBranch(branchList);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch dealer information.');
    }
  };

  useEffect(() => {
    if (realDealerId) {
      fetchVehicle();
      if (!vehicleDealerInfo) {
        fetchDealer();
      }
    } else {
        setLoading(false);
        setError("Invalid Dealer Link");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, hashId, currentPage, itemsPerPage, searchQuery, condition]); 

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, location.search, condition]);

  if (error) return <NotFoundPage />;

  const handleFilterChange = (key: string, value: string) => {
    const currentParams = new URLSearchParams(location.search);
    const existing = currentParams.get(key);
    let updatedValues: string[] = [];
    
    if (existing) {
      const values = existing.split(',');
      if (values.includes(value)) {
        updatedValues = values.filter((v) => v !== value);
      } else {
        updatedValues = [...values, value];
      }
      if (updatedValues.length > 0) {
        currentParams.set(key, updatedValues.join(','));
      } else {
        currentParams.delete(key);
      }
    } else {
      updatedValues = [value];
      currentParams.set(key, value);
    }
    
    currentParams.delete('page');
    navigate(`/dealers/${hashId}?${currentParams.toString()}`);
  };

  // ✅ Handle Switching Between Used and New
  const handleConditionChange = (_event: React.SyntheticEvent, newValue: string) => {
    const currentParams = new URLSearchParams(location.search);
    
    // Clear out filters when switching to avoid incompatible query strings
    Array.from(currentParams.keys()).forEach(key => currentParams.delete(key));
    
    currentParams.set('condition', newValue);
    setSearchQuery(''); // Reset search
    navigate(`/dealers/${hashId}?${currentParams.toString()}`);
  };

  const googleMapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(
    vehicleDealerInfo?.address || ''
  )}&t=&z=15&ie=UTF8&iwloc=&output=embed`;

  const handleGmailOpen = (email: string) => {
    const subject = encodeURIComponent('Enquiry from Work Horse website');
    const body = encodeURIComponent(
      `Hi,\n\nI'm interested in one of the vehicles listed on the Work Horse website.\n\nCould you please provide more details or arrange for a viewing?\n\nThank you.`
    );
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${subject}&body=${body}`;
    window.open(gmailUrl, '_blank');
  };

  const ContactRow = ({ icon, text, label, onClick, link }: any) => (
    <Tooltip title={label} arrow placement="bottom-start">
        <Box 
            display="flex" 
            alignItems="center" 
            gap={2} 
            sx={{ 
                opacity: 0.9, 
                width: 'fit-content', 
                cursor: (onClick || link) ? 'pointer' : 'default',
                '&:hover': { opacity: 1, color: 'primary.main' }
            }}
            onClick={onClick}
        >
            <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 400 }}>
                {text}
            </Typography>
        </Box>
    </Tooltip>
  );

  const indexOfFirstRecord = (currentPage - 1) * itemsPerPage + 1;
  const indexOfLastRecord = Math.min(currentPage * itemsPerPage, totalRecords);

  return (
    <Box sx={{ bgcolor: mode === 'dark' ? 'background.default' : '#f4f6f8', minHeight: '100vh' }}>
      <Header />
      
      <Container maxWidth="xl" sx={{ py: 8 }}>
        {loading && !vehicleDealerInfo ? (
          <Paper elevation={0} sx={{ p: 4, borderRadius: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Paper>
        ) : (
          <Box>
            {vehicleDealerInfo && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  mb: 6,
                  borderRadius: 4,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: mode === 'dark' ? '#1e1e1e' : '#fff',
                  boxShadow: '0px 4px 20px rgba(0,0,0,0.05)'
                }}
              >
                <Grid container spacing={6}>
                  <Grid size={{ xs: 12, md: 5 }} >
                    <Box display="flex" flexDirection="column" gap={3}>
                      <Box display="flex" alignItems="center" gap={3}>
                        {vehicleDealerInfo.imageUrl && (
                          <Box component="img" src={vehicleDealerInfo.imageUrl} alt={vehicleDealerInfo.dealerName} sx={{ width: 80, height: 80, borderRadius: 2, objectFit: 'contain', border: '1px solid', borderColor: 'divider', p: 1 }} />
                        )}
                        <Box>
                            <Typography variant="h5" fontWeight={700} sx={{ color: 'text.primary', lineHeight: 1.2 }}>
                                {vehicleDealerInfo.dealerName}
                            </Typography>
                        </Box>
                      </Box>

                      <Divider />

                      <Stack spacing={2}>
                        {vehicleDealerInfo.address && <ContactRow icon={<LocationOnIcon />} text={vehicleDealerInfo.address} label="Showroom Address" />}
                        {otherBranch?.length > 0 && (
                             <Tooltip title="View other showroom locations" arrow placement="bottom-start">
                                <Button startIcon={<StorefrontIcon />} onClick={() => setBranchDialogOpen(true)} sx={{ justifyContent: 'flex-start', pl: 0 }}>
                                    View {otherBranch.length} other branch{otherBranch.length > 1 ? 'es' : ''}
                                </Button>
                             </Tooltip>
                        )}
                        <OtherBranchDialog branchDialogOpen={branchDialogOpen} setBranchDialogOpen={setBranchDialogOpen} otherBranch={otherBranch} vehicleDealerInfo={vehicleDealerInfo} />
                        {vehicleDealerInfo.officeNumber && <ContactRow icon={<BusinessIcon />} text={vehicleDealerInfo.officeNumber} label="Office Number" />}
                        {vehicleDealerInfo.faxNumber && <ContactRow icon={<FaxIcon />} text={vehicleDealerInfo.faxNumber} label="Fax Number" />}
                        {vehicleDealerInfo.contactNumber && (
                           <a href={`tel:${vehicleDealerInfo.contactNumber}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                <ContactRow icon={<SmartphoneIcon />} text={vehicleDealerInfo.contactNumber} label="Direct Contact (Click to Call)" link />
                           </a>
                        )}
                        {vehicleDealerInfo.website && (
                            <a href={vehicleDealerInfo.website} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                                <ContactRow icon={<LanguageIcon />} text={vehicleDealerInfo.website} label="Visit Website" link />
                            </a>
                        )}
                        {vehicleDealerInfo.dealerEmail && (
                            <ContactRow icon={<EmailIcon />} text={vehicleDealerInfo.dealerEmail} label="Click to Email" onClick={() => handleGmailOpen(vehicleDealerInfo.dealerEmail)} />
                        )}
                      </Stack>

                      {vehicleDealerInfo.dealerEmail && (
                        <Box mt={1}>
                          <Tooltip title="Send an email enquiry directly to this dealer" arrow placement="bottom">
                            <Button variant="contained" size="large" startIcon={<EmailIcon />} onClick={() => handleGmailOpen(vehicleDealerInfo.dealerEmail)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 4, py: 1.5 }}>
                                Contact Seller
                            </Button>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, md: 7 }}>
                    <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', height: '100%', minHeight: 350, border: '1px solid', borderColor: 'divider' }}>
                      <iframe title="Dealer Location Map" width="100%" height="100%" style={{ border: 0, minHeight: '350px' }} loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" src={googleMapSrc} />
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* ✅ Condition Tabs */}
            <Box mb={4}>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Available Inventory
              </Typography>
              <Typography variant="body1" color="text.secondary" mb={3}>
                 Browse vehicles currently listed by {vehicleDealerInfo?.dealerName || 'this dealer'}.
              </Typography>

              <Tabs 
                value={condition} 
                onChange={handleConditionChange} 
                textColor="primary"
                indicatorColor="primary"
                sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="Used Vehicles" value="used" sx={{ fontWeight: 600, textTransform: 'none', fontSize: '1rem' }} />
                <Tab label="New Vehicles" value="new" sx={{ fontWeight: 600, textTransform: 'none', fontSize: '1rem' }} />
              </Tabs>
              
              <Typography variant="h6" fontWeight="800" color="text.primary" sx={{ mb: 2 }}>
                {totalRecords} {totalRecords === 1 ? 'Vehicle' : 'Vehicles'} Found
              </Typography>

              {/* ✅ Dynamic Filter Bar based on Condition */}
              {condition === 'new' ? (
                <NewCarFilterBar
                  viewMode="list"
                  onToggleViewMode={() => {}}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  onFilterChange={handleFilterChange}
                  url={`/dealers/${hashId}`}
                />
              ) : (
                <CarFilterBar
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  url={`/dealers/${hashId}`}
                  onFilterChange={handleFilterChange}
                />
              )}
            </Box>

            {/* Listing Section */}
            {loading ? (
              <Box display="flex" justifyContent="center" py={10}>
                <CircularProgress />
              </Box>
            ) : vehicleList && vehicleList.length > 0 ? (
              <>
                <Stack spacing={3}>
                  {/* ✅ Dynamic Card Render based on Condition */}
                  {vehicleList.map((v) => (
                    condition === 'new' 
                      ? <NewCarCard key={v.newVehicleId || v.vehicleId} vehicle={v} viewMode="list" />
                      : <CarCard key={v.vehicleId || v.newVehicleId} vehicle={v} viewMode="list" />
                  ))}
                </Stack>

                <Box mt={6} display="flex" flexDirection="column" alignItems="center">
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Showing {indexOfFirstRecord} to {indexOfLastRecord} of {totalRecords} Vehicles
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, page) => setCurrentPage(page)}
                    color="primary"
                    size="large"
                    shape="rounded"
                  />
                </Box>
              </>
            ) : (
              <Paper elevation={0} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, border: '1px dashed', borderColor: 'divider', borderRadius: 4, bgcolor: 'transparent' }}>
                <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">
                  No {condition} vehicles found
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3} maxWidth={400} textAlign="center">
                  This dealer currently has no {condition} vehicles listed matching your criteria. Try adjusting your filters or check back later.
                </Typography>
                <Button 
                    variant="outlined" 
                    onClick={() => {
                        const clearParams = new URLSearchParams();
                        clearParams.set('condition', condition);
                        navigate(`/dealers/${hashId}?${clearParams.toString()}`);
                    }}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Clear Filters
                </Button>
              </Paper>
            )}
          </Box>
        )}
      </Container>
      <Footer />
    </Box>
  );
};

export default DealerVehicleListing;